import { ApiDataSourceSpec, apiDataSource } from "@/io/datasource/ApiDatasource.ts";
import { SearchForm, SearchTable, searchTableSchema } from "@/models/AnterioritySearchModel.ts";
import {
  CarePlanDirectives,
  PatientCreationDirectives,
  carePlanDirectivesSchema,
  patientCreationDirectivesSchema,
} from "@/models/CarePlanDirectivesModel.ts";
import { InterruptCarePlanData } from "@/models/CarePlanInterruptionModel.ts";
import { CarePlan, CurrentCarePlanInfo, carePlanSchema, currentCarePlanInfoSchema } from "@/models/CarePlanModel.ts";
import { CareTeam, careTeamSchema } from "@/models/CareTeamModel.ts";
import { Contact, CreateContact, contactSchema } from "@/models/ContactModel.ts";
import { CreatePatientModel } from "@/models/CreatePatientModel.ts";
import { DiabetesDataDisplayParams, diabetesDisplayModeSchema } from "@/models/DiabetesDisplayModeModel.ts";
import { EditTherapeuticObjectivesData } from "@/models/EditTherapeuticObjectivesModel.ts";
import { ExistingPatient, ExistingPatientQuery, existingPatientSchema } from "@/models/ExistingPatientModel.ts";
import { FileModel } from "@/models/FileModel.ts";
import { GroupedInclusionPatient, groupedInclusionPatientModelSchema } from "@/models/GroupedInclusionModel.ts";
import { HomeServiceProvider, homeServiceProviderSchema } from "@/models/HomeServiceProviderModel.ts";
import { ValidateOrRejectIdentity } from "@/models/identity/FrenchIdentity.ts";
import { IdentityValidation, identityValidationSchema } from "@/models/identity/IdentityValidationModel.ts";
import { InclusionAgentPatient, inclusionAgentPatientModelSchema } from "@/models/InclusionAgentModel.ts";
import {
  CreateNurseInclusionSuggestionRequest,
  NurseInclusionPatient,
  NursePreInclusionDirectives,
  nurseInclusionPatientModelSchema,
  nursePreInclusionDirectivesSchema,
} from "@/models/NurseInclusionPatientModel.ts";
import {
  reinclusionStateForNurseSchema,
  type ReinclusionStateForNurse,
  reinclusionStateForDoctorSchema,
  type ReinclusionStateForDoctor,
} from "@/models/NurseReinclusionRequestModel.ts";
import { PatientEvent, patientEventsSchema } from "@/models/PatientEventModel.ts";
import { Patient, PatientID, patientIDSchema, patientSchema } from "@/models/PatientModel.ts";
import { PatientRenewalInfo, RenewType, patientRenewalInfoSchema } from "@/models/PatientRenewalInfoModel.ts";
import { PatientListByCategory, patientListByCategorySchema } from "@/models/PatientSummaryModel.ts";
import { type RefuseReinclusionReason } from "@/models/ReinclusionAgentSuggestionModel.ts";
import { RenewalNudge, renewalNudgeSchema } from "@/models/RenewalNudgeModel.ts";
import { RenewCarePlanData } from "@/models/RenewCarePlanModel.ts";
import { CarePlanCreationInfo } from "@/uiKit/organisms/PatientCreationAndRenewal/CarePlan/CarePlanCreation.ts";
import { PatientInfo } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientInfo/PatientInfo.ts";
import { cz } from "@/utils/zodUtils.ts";

export type PatientCreationSavedData = {
  patientInfo: Partial<PatientInfo>;
  carePlan: Partial<CarePlanCreationInfo>;
};
/**
 * For health pro only
 */
export class ManagePatientRepository {
  constructor(private readonly datasource: ApiDataSourceSpec = apiDataSource) {}

  getNursePatientList = (): Promise<PatientListByCategory> => {
    return this.datasource.get(`practitioner/patients/list/nurse/`, patientListByCategorySchema);
  };

  getDoctorPatientList = (): Promise<PatientListByCategory> => {
    return this.datasource.get(`practitioner/patients/list/doctor/`, patientListByCategorySchema);
  };

  getPatient = (id: string): Promise<Patient> => {
    return this.datasource.get(`practitioners/patients/${id}/`, patientSchema);
  };

  createPatient = (data: CreatePatientModel): Promise<PatientID> => {
    return this.datasource.post(`practitioners/patients/create/`, patientIDSchema, data);
  };

  findExistingPatient = (data: ExistingPatientQuery): Promise<ExistingPatient> => {
    return this.datasource.post(`practitioners/patients/check-existing/`, existingPatientSchema, data);
  };

  renewCarePlan = (patientId: string, data: RenewCarePlanData): Promise<PatientID> => {
    return this.datasource.post(`practitioners/renew-care-plan/${patientId}/`, patientIDSchema, data);
  };

  editTherapeuticObjectives = (
    patientId: string,
    carePlanId: string,
    data: EditTherapeuticObjectivesData,
  ): Promise<undefined> => {
    return this.datasource.patch(`practitioner/care-plan/${patientId}/${carePlanId}/description/`, cz.ignore(), {
      description: data.description,
      description_attachment: data.descriptionAttachment,
    });
  };

  editTreatmentPrescription = (patientId: string, carePlanId: string, data: FileModel): Promise<undefined> => {
    return this.datasource.patch(`practitioner/care-plan/${patientId}/${carePlanId}/treatment/`, cz.ignore(), {
      treatment_prescription: data,
    });
  };

  deleteTreatmentPrescription = (patientId: string, carePlanId: string): Promise<undefined> => {
    return this.datasource.delete(`practitioner/care-plan/${patientId}/${carePlanId}/treatment/`, cz.ignore());
  };

  interruptCarePlan = (patientId: string, data: InterruptCarePlanData): Promise<undefined> => {
    return this.datasource.post(`practitioners/interrupt-care-plan/${patientId}/`, cz.ignore(), data);
  };

  editReferralLetter = (patientId: string, carePlanId: string, data: FileModel): Promise<undefined> => {
    return this.datasource.patch(`practitioner/care-plan/${patientId}/${carePlanId}/referral/`, cz.ignore(), {
      referral_letter: data,
    });
  };

  deleteReferralLetter = (patientId: string, carePlanId: string): Promise<undefined> => {
    return this.datasource.delete(`practitioner/care-plan/${patientId}/${carePlanId}/referral/`, cz.ignore());
  };

  getCareTeam = (patientId: string): Promise<CareTeam> => {
    return this.datasource.get(`practitioners/patients/${patientId}/care-team/`, careTeamSchema);
  };

  carePlansHistory = (patientId: string): Promise<CarePlan[]> => {
    return this.datasource.get(`practitioner/care-plan/${patientId}/history/`, cz.forcedArray(carePlanSchema));
  };

  getCurrentCarePlanInfo = (patientId: string): Promise<CurrentCarePlanInfo> => {
    return this.datasource.get(`practitioners/patients/${patientId}/care-plans/`, currentCarePlanInfoSchema);
  };

  getCarePlanDirectives = (directivesType: RenewType, patientId: string): Promise<CarePlanDirectives> => {
    return this.datasource.get(
      `practitioners/care-plan-directives/${directivesType}/${patientId}/`,
      carePlanDirectivesSchema,
    );
  };

  getPatientCreationDirectives = (preinclusionId: string | undefined): Promise<PatientCreationDirectives> => {
    return this.datasource.get(`practitioners/patient-creation-directives/`, patientCreationDirectivesSchema, {
      params: {
        preinclusion_id: preinclusionId,
      },
    });
  };

  getListContacts = (patientId: string): Promise<Contact[]> => {
    return this.datasource.get(`practitioner/contacts/${patientId}/`, cz.forcedArray(contactSchema));
  };

  getIdentitiesToValidate = (): Promise<IdentityValidation[]> => {
    return this.datasource.get(`practitioners/identities-to-validate/`, cz.forcedArray(identityValidationSchema));
  };

  getDiabetesDisplayMode = (patientId: string): Promise<DiabetesDataDisplayParams> => {
    return this.datasource.get(`practitioner/patients/${patientId}/diabetes-display-mode/`, diabetesDisplayModeSchema);
  };

  validateOrRejectIdentity = (patientId: string, action: ValidateOrRejectIdentity): Promise<PatientID> => {
    return this.datasource.post(`practitioners/patients/${patientId}/validate-identity/${action}/`, patientIDSchema);
  };

  createPatientContacts = (patientId: string, data: CreateContact): Promise<undefined> => {
    return this.datasource.post(`practitioner/contacts/${patientId}/`, cz.ignore(), data);
  };

  updatePatientContacts = (patientId: string, contactId: string, data: Partial<CreateContact>): Promise<undefined> => {
    return this.datasource.patch(`practitioner/contacts/${patientId}/${contactId}/`, cz.ignore(), data);
  };

  deletePatientContacts = (patientId: string, contactId: string): Promise<undefined> => {
    return this.datasource.delete(`practitioner/contacts/${patientId}/${contactId}/`, cz.ignore());
  };

  getReinclusionStateForNurse = (patientId: string): Promise<ReinclusionStateForNurse> => {
    return this.datasource.get(`practitioner/reinclusion/${patientId}/current/`, reinclusionStateForNurseSchema);
  };

  getReinclusionStateForDoctor = (patientId: string): Promise<ReinclusionStateForDoctor> => {
    return this.datasource.get(`practitioner/reinclusion/${patientId}/current/`, reinclusionStateForDoctorSchema);
  };

  requestReinclusion = (patientId: string, nurseMessage: string | undefined): Promise<undefined> => {
    return this.datasource.post(
      `practitioner/reinclusion/${patientId}/request/`,
      cz.ignore(),
      nurseMessage ? { nurse_message: nurseMessage } : {},
    );
  };

  cancelReinclusion = (patientId: string): Promise<undefined> => {
    return this.datasource.post(`practitioner/reinclusion/${patientId}/cancel/`, cz.ignore());
  };

  refuseReinclusion = (patientId: string): Promise<undefined> => {
    return this.datasource.post(`practitioner/reinclusion/${patientId}/refuse/`, cz.ignore());
  };

  acceptReinclusionAgentSuggestion = (patientId: string, nurseMessage: string | undefined): Promise<undefined> => {
    return this.datasource.post(
      `practitioner/reinclusion/${patientId}/suggestion/accept/`,
      cz.ignore(),
      nurseMessage ? { nurse_message: nurseMessage } : {},
    );
  };

  refuseReinclusionAgentSuggestion = (
    patientId: string,
    reason: RefuseReinclusionReason,
    reasonFreeText: string | undefined,
  ): Promise<undefined> => {
    return this.datasource.post(
      `practitioner/reinclusion/${patientId}/suggestion/refuse/`,
      cz.ignore(),
      reason === "other" ? { reason, reason_free_text: reasonFreeText } : { reason },
    );
  };

  getRenewalNudge = (patientId: string): Promise<RenewalNudge> => {
    return this.datasource.get(`practitioner/renewal-nudge/${patientId}/`, renewalNudgeSchema);
  };

  refuseRenewal = (patientId: string): Promise<undefined> => {
    return this.datasource.post(`practitioner/renewal-nudge/${patientId}/`, cz.ignore());
  };

  listHSP = (): Promise<HomeServiceProvider[]> => {
    return this.datasource.get(
      "/practitioner/home-service-providers/list/all/",
      cz.forcedArray(homeServiceProviderSchema),
    );
  };

  getPatientEvents = (patientId: string): Promise<PatientEvent[]> => {
    return this.datasource.get(`practitioner/events/${patientId}/`, cz.forcedArray(patientEventsSchema));
  };

  getPatientRenewalInfo = (patientId: string): Promise<PatientRenewalInfo> => {
    return this.datasource.get(`practitioner/renewal-status/${patientId}/`, patientRenewalInfoSchema);
  };

  getInclusionAgentPatients = (): Promise<InclusionAgentPatient[]> => {
    return this.datasource.get(`practitioner/inclusion-agent/`, cz.forcedArray(inclusionAgentPatientModelSchema));
  };

  getGroupedInclusionPatients = (): Promise<GroupedInclusionPatient[]> => {
    return this.datasource.get(`practitioner/grouped-inclusion/`, cz.forcedArray(groupedInclusionPatientModelSchema));
  };

  acceptInclusionAgentSuggestion = (inclusionAgentId: string, phoneNumber: string): Promise<undefined> => {
    return this.datasource.post(`practitioner/inclusion-agent/${inclusionAgentId}/accept/`, cz.ignore(), {
      phone_number: phoneNumber,
    });
  };

  rejectInclusionAgentSuggestion = (inclusionAgentId: string): Promise<undefined> => {
    return this.datasource.post(`practitioner/inclusion-agent/${inclusionAgentId}/reject/`, cz.ignore());
  };

  anterioritySearchPatient = (searchQuery: SearchForm): Promise<SearchTable[]> => {
    return this.datasource.post("practitioner/anteriority-search/", cz.forcedArray(searchTableSchema), searchQuery);
  };

  // Nurse Inclusion - Doctor endpoints
  getNurseInclusionPatients = (): Promise<NurseInclusionPatient[]> => {
    return this.datasource.get(`practitioner/nurse-inclusion/`, cz.forcedArray(nurseInclusionPatientModelSchema));
  };

  rejectNurseInclusionPatient = (nurseInclusionPatientId: string): Promise<undefined> => {
    return this.datasource.post(`practitioner/nurse-inclusion/${nurseInclusionPatientId}/reject/`, cz.ignore());
  };

  // Nurse Inclusion - Nurse endpoints
  getNurseInclusionDirectives = (): Promise<NursePreInclusionDirectives> => {
    return this.datasource.get(`practitioner/nurse-inclusion-suggest/directives/`, nursePreInclusionDirectivesSchema);
  };

  getNurseInclusionSuggestions = (): Promise<NurseInclusionPatient[]> => {
    return this.datasource.get(
      `practitioner/nurse-inclusion-suggest/`,
      cz.forcedArray(nurseInclusionPatientModelSchema),
    );
  };

  createNurseInclusionSuggestion = (data: CreateNurseInclusionSuggestionRequest): Promise<NurseInclusionPatient> => {
    return this.datasource.post(`practitioner/nurse-inclusion-suggest/`, nurseInclusionPatientModelSchema, data);
  };

  cancelNurseInclusionSuggestion = (nurseInclusionPatientId: string): Promise<undefined> => {
    return this.datasource.post(`practitioner/nurse-inclusion-suggest/${nurseInclusionPatientId}/cancel/`, cz.ignore());
  };
}
